import React, { useState, useMemo } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// --- Types ---

export interface ScreeningAnalysisProps {
  /** Screening answers: question -> answer map */
  screeningAnswers: Record<string, string> | null;
  /** Review concerns from the AI review (rawResponse red flags) */
  reviewConcerns?: string[];
  /** Additional CSS classes */
  className?: string;
}

export type ScreeningVerdict = 'pass' | 'concern' | 'knockout';

export interface AnalyzedAnswer {
  question: string;
  answer: string;
  verdict: ScreeningVerdict;
  reason?: string;
}

// --- Detection Logic ---

/** Negative answer patterns (case insensitive) */
const NEGATIVE_PATTERNS = [
  /^no$/i,
  /^none$/i,
  /\bwill not\b/i,
  /\bcannot\b/i,
  /\bcan't\b/i,
  /\bunable\b/i,
  /\bnot willing\b/i,
  /\bnot interested\b/i,
  /\bnot available\b/i,
  /\bdo not\b/i,
  /\bdon't\b/i,
  /^0\s*(years?|yrs?|months?|mos?)?$/i,
  /\b0 years?\b/i,
  /\bnever\b/i,
];

/** Question patterns that make a negative answer a knockout */
const KNOCKOUT_QUESTION_PATTERNS = [
  /relocat/i,
  /willing to travel/i,
  /authorized to work/i,
  /work authorization/i,
  /legally (authorized|eligible|permitted)/i,
  /right to work/i,
  /visa\s*(status|sponsor)/i,
  /background check/i,
  /drug (test|screen)/i,
  /security clearance/i,
  /start (date|immediately|within)/i,
  /available to start/i,
  /minimum.*salary/i,
  /salary.*expectation/i,
  /required.*certification/i,
  /required.*license/i,
  /18 years.*age/i,
  /legally.*age/i,
];

/** Ambiguous answer patterns */
const AMBIGUOUS_PATTERNS = [
  /\bmaybe\b/i,
  /\bpossibly\b/i,
  /\bnot sure\b/i,
  /\bdepends\b/i,
  /\buncertain\b/i,
  /\bprefer not\b/i,
  /\bprobably not\b/i,
  /\bnot at this time\b/i,
  /\bnegotiable\b/i,
];

function isNegativeAnswer(answer: string): boolean {
  const trimmed = answer.trim();
  return NEGATIVE_PATTERNS.some((pattern) => pattern.test(trimmed));
}

function isAmbiguousAnswer(answer: string): boolean {
  const trimmed = answer.trim();
  return AMBIGUOUS_PATTERNS.some((pattern) => pattern.test(trimmed));
}

function isKnockoutQuestion(question: string): boolean {
  return KNOCKOUT_QUESTION_PATTERNS.some((pattern) => pattern.test(question));
}

function analyzeScreeningAnswers(
  screeningAnswers: Record<string, string>,
  reviewConcerns: string[] = []
): AnalyzedAnswer[] {
  const results: AnalyzedAnswer[] = [];

  for (const [question, answer] of Object.entries(screeningAnswers)) {
    const answerStr = String(answer ?? '');
    const negative = isNegativeAnswer(answerStr);
    const ambiguous = isAmbiguousAnswer(answerStr);
    const knockout = isKnockoutQuestion(question);

    // Cross-reference with review concerns
    const questionWords = question.toLowerCase().split(/\s+/);
    const matchesConcern = reviewConcerns.some((concern) => {
      const concernLower = concern.toLowerCase();
      return questionWords.some(
        (word) => word.length > 3 && concernLower.includes(word)
      );
    });

    let verdict: ScreeningVerdict = 'pass';
    let reason: string | undefined;

    if (negative && knockout) {
      verdict = 'knockout';
      reason = 'Negative answer to critical screening question';
    } else if (negative) {
      verdict = 'concern';
      reason = 'Negative answer detected';
    } else if (ambiguous && knockout) {
      verdict = 'concern';
      reason = 'Ambiguous answer to critical question';
    } else if (ambiguous) {
      verdict = 'concern';
      reason = 'Ambiguous answer may need follow-up';
    } else if (matchesConcern) {
      verdict = 'concern';
      reason = 'Related to AI review concern';
    }

    results.push({ question, answer: answerStr, verdict, reason });
  }

  // Sort: knockouts first, then concerns, then passes
  const order: Record<ScreeningVerdict, number> = {
    knockout: 0,
    concern: 1,
    pass: 2,
  };
  results.sort((a, b) => order[a.verdict] - order[b.verdict]);

  return results;
}

// --- Sub-components ---

const VerdictIcon: React.FC<{ verdict: ScreeningVerdict }> = ({ verdict }) => {
  switch (verdict) {
    case 'pass':
      return <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 flex-shrink-0" />;
    case 'concern':
      return <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400 flex-shrink-0" />;
    case 'knockout':
      return <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0" />;
  }
};

const VerdictBadge: React.FC<{ verdict: ScreeningVerdict }> = ({ verdict }) => {
  const classes: Record<ScreeningVerdict, string> = {
    pass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    concern:
      'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    knockout:
      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold',
  };

  const labels: Record<ScreeningVerdict, string> = {
    pass: 'Pass',
    concern: 'Concern',
    knockout: 'Knockout',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${classes[verdict]}`}
    >
      <VerdictIcon verdict={verdict} />
      {labels[verdict]}
    </span>
  );
};

// --- Main Component ---

export const ScreeningAnalysis: React.FC<ScreeningAnalysisProps> = ({
  screeningAnswers,
  reviewConcerns = [],
  className = '',
}) => {
  const analyzed = useMemo(
    () =>
      screeningAnswers && Object.keys(screeningAnswers).length > 0
        ? analyzeScreeningAnswers(screeningAnswers, reviewConcerns)
        : [],
    [screeningAnswers, reviewConcerns]
  );

  const knockoutCount = analyzed.filter((a) => a.verdict === 'knockout').length;
  const concernCount = analyzed.filter((a) => a.verdict === 'concern').length;
  const hasIssues = knockoutCount > 0 || concernCount > 0;

  // Expanded by default if there are concerns or knockouts
  const [isExpanded, setIsExpanded] = useState(hasIssues);

  // If no screening answers, render nothing
  if (!screeningAnswers || Object.keys(screeningAnswers).length === 0) {
    return null;
  }

  // Determine the header border color
  const borderColorClass = knockoutCount > 0
    ? 'border-l-red-500 dark:border-l-red-400'
    : concernCount > 0
      ? 'border-l-amber-500 dark:border-l-amber-400'
      : 'border-l-green-500 dark:border-l-green-400';

  // Header icon
  const HeaderIcon = hasIssues ? ShieldAlert : ShieldCheck;
  const headerIconColor = knockoutCount > 0
    ? 'text-red-500 dark:text-red-400'
    : concernCount > 0
      ? 'text-amber-500 dark:text-amber-400'
      : 'text-green-500 dark:text-green-400';

  return (
    <div
      className={`rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${borderColorClass} bg-white dark:bg-gray-800 overflow-hidden ${className}`}
    >
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <HeaderIcon className={`h-5 w-5 ${headerIconColor}`} />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Screening Analysis
          </h4>
          {/* Summary badges */}
          <div className="flex items-center gap-1.5 ml-1">
            {knockoutCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">
                <XCircle className="h-3 w-3" />
                {knockoutCount}
              </span>
            )}
            {concernCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
                <AlertTriangle className="h-3 w-3" />
                {concernCount}
              </span>
            )}
            {!hasIssues && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                <CheckCircle className="h-3 w-3" />
                All clear
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {analyzed.length} question{analyzed.length !== 1 ? 's' : ''}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {/* Answer List */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {analyzed.map((item, index) => (
              <div
                key={index}
                className={`px-4 py-3 ${
                  item.verdict === 'knockout'
                    ? 'bg-red-50/50 dark:bg-red-950/20'
                    : item.verdict === 'concern'
                      ? 'bg-amber-50/50 dark:bg-amber-950/20'
                      : ''
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {item.question}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">
                      {item.answer}
                    </p>
                    {item.reason && (
                      <p className={`text-xs mt-1 ${
                        item.verdict === 'knockout'
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-amber-600 dark:text-amber-400'
                      }`}>
                        {item.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 mt-0.5">
                    <VerdictBadge verdict={item.verdict} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ScreeningAnalysis.displayName = 'ScreeningAnalysis';
